import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axiosUI from '../../axiosUI';
import { Link } from '@reach/router';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Main from '../Main/Main';
import Button from '../Button/Button';
import Loader from '../Loader/Loader';
import Input, { INPUT_TYPES } from '../Input/Input';
import PopupWrapper from '../PopupWrapper';
import ChangesetRow from './ChangesetRow';
import {
  NOTIFICATIONS,
  SALESCHANNEL_NAME_MAP,
  LOCALE_NAME_MAP,
  RESOURCE_STATES,
  CONTEXT_FIELD_TYPE,
  CONTEXT_MAP,
  CONTEXT_GROUPS,
} from '../../constants';
import { withGlobalContext } from '../../App';
import withCommonUtils from '../withCommonUtils';
const { DRAFT, PUBLISHED } = RESOURCE_STATES;
const { INCLUDE, EXCLUDE } = CONTEXT_GROUPS;

class ChannelingChangeset extends Component {
  static propTypes = {
    language: PropTypes.string,
    country: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      drafts: [],
      selected: {},
    };
  }

  componentDidMount = () => {
    this.setState({ loading: true }, async () => {
      await this.fetchDraftsAndInitializeState();
      this.setState({ loading: false });
    });
  };

  fetchDraftsAndInitializeState = async () => {
    const channelUrl = `/api/channels`;
    const openingHourUrl = `/api/opening-hours`;
    const commonParams = {
      salesChannel: this.props.country,
      locale: this.props.language,
    };
    try {
      const [
        channelResponse,
        openingHourResponse,
      ] = await Promise.all([
        axiosUI.get(channelUrl, {
          params: {
            ...commonParams,
            state: DRAFT,
          },
        }),
        axiosUI.get(openingHourUrl, {
          params: {
            ...commonParams,
            state: PUBLISHED,
          },
        }),
      ]);

      const openingHourIdToDataMap = openingHourResponse.data.reduce(
        (map, oph) => {
          map[oph.id] = oph.name;
          return map;
        },
        {}
      );

      const transformedChannels = channelResponse.data.map(channel => {
        return {
          ...channel,
          [INCLUDE]: this.transformContextsComingFromBackend(channel[INCLUDE]),
          [EXCLUDE]: this.transformContextsComingFromBackend(channel[EXCLUDE]),
          opening_hour_name: openingHourIdToDataMap[channel.opening_hour_id],
        };
      });

      const selected = transformedChannels.reduce((map, channel) => {
        const key = this.getUniqueKey(channel);
        map[key] = true;
        return map;
      }, {});

      this.setState({
        drafts: transformedChannels,
        selected,
      });
    } catch (err) {
      console.log(err);
    }
  };

  transformContextsComingFromBackend = (contextArray) => {
    const contextsMap = contextArray.reduce((contextGroup, context) => {
      const { key, value } = context;
      const isMultiSelect = CONTEXT_FIELD_TYPE[context.key] === 'multi_select';
      contextGroup[context.key] = contextGroup[context.key] || {
        id: context.id,
        key,
        value: isMultiSelect ? [] : '',
      };
      if (isMultiSelect) {
        contextGroup[context.key].value.push(value);
      } else {
        contextGroup[context.key].value = value;
      }

      return contextGroup;
    }, {});
    const contexts = Object.keys(contextsMap).map(key => contextsMap[key]);

    return contexts;
  };

  getUniqueKey = draft => {
    return `${draft.id}_${draft.variant}`;
  };

  getSelectedItems = () => {
    return this.state.drafts.filter(
      draft => this.state.selected[this.getUniqueKey(draft)]
    );
  };

  toggleDiscardWarningPopup = () => {
    this.setState(prevState => ({
      showDiscardWarningPopup: !prevState.showDiscardWarningPopup,
    }));
  };

  discardChanges = async () => {
    const itemsToDelete = this.getSelectedItems();
    try {
      await axiosUI.delete(`/api/channels/`, {
        data: itemsToDelete.map(item => ({
          id: item.id,
          variant: item.variant,
          sales_channel_id: item.sales_channel_id,
          locale: item.locale,
        })),
      });

      this.toggleDiscardWarningPopup();
      this.props.createNotification({
        message: `Changes are discarded`,
        type: NOTIFICATIONS.TYPE.INFO,
        timeout: 5000,
      });

      await this.fetchDraftsAndInitializeState();
    } catch (err) {
      console.log(err);
      this.toggleDiscardWarningPopup();

      this.props.createNotification({
        message: `Failed to discard changes`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }
  };

  approveChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToApprove = this.getSelectedItems();

      try {
        await axiosUI.post(
          `/api/channels/approve`,
          itemsToApprove.map(item => ({
            id: item.id,
            variant: item.variant,
          }))
        );

        this.props.createNotification({
          message: `Changes are approved`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });

        await this.fetchDraftsAndInitializeState();
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to approve changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  publishChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToPublish = this.getSelectedItems();

      try {
        await axiosUI.post(
          `/api/channels/publish`,
          itemsToPublish.map(item => ({
            id: item.id,
            variant: item.variant,
          }))
        );

        this.props.createNotification({
          message: `Changes are published`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });

        await this.fetchDraftsAndInitializeState();
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to publish changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  toggleSelectAllRows = initialValue => {
    const selections = this.state.drafts.reduce((map, channel) => {
      map[this.getUniqueKey(channel)] = !initialValue;
      return map;
    }, {});
    this.setState({ selected: selections });
  };

  toggleRowSelection = (event, draft) => {
    this.setState({
      selected: {
        ...this.state.selected,
        [this.getUniqueKey(draft)]: event.target.checked,
      },
    });
  };

  render() {
    const selectedItems = this.getSelectedItems();
    const allAreSelected = selectedItems.length === this.state.drafts.length;
    const nonNullSelectedItems = Boolean(selectedItems.length);
    const canPublishSelectedChanges =
      nonNullSelectedItems &&
      selectedItems.every(
        draft => draft.first_approver && draft.second_approver
      );

    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={3} />
        <Topbar className="banners_changeset__topbar">
          <span className="font-bold text-lg capitalize">
            <Link className="bb-1-blue no-underline text-blue" to="/channeling">
              Channeling
            </Link>{' '}
            /{' '}
            <Link
              className="bb-1-blue no-underline text-blue"
              to={`/channeling/${this.props.country}/${this.props.language}`}>
              {SALESCHANNEL_NAME_MAP[this.props.country]} -{' '}
              {LOCALE_NAME_MAP[this.props.language]}
            </Link>{' '}
            / Review Changes
          </span>
          <span className="float-right text-right">
            <PopupWrapper
              className="relative"
              isOpen={this.state.showDiscardWarningPopup}
              togglePopup={this.toggleDiscardWarningPopup}>
              <Button
                type="red-transparent"
                className="h-8"
                // disabled={!nonNullSelectedItems}
                onClick={this.toggleDiscardWarningPopup}>
                Discard Changes
              </Button>
              <AnimatePresence>
                {this.state.showDiscardWarningPopup && (
                  <motion.div
                    initial={{ opacity: 0, y: '80%' }}
                    animate={{ opacity: 1, y: '100%' }}
                    exit={{ opacity: 0, y: '80%' }}
                    transition={{
                      ease: [0.18, 0.89, 0.39, 1.4],
                      duration: 0.3,
                    }}
                    className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                    <div className="text-center leading-normal">
                      Are you sure you want to discard selected changes?
                    </div>
                    <div className="flex justify-around mt-8">
                      <Button
                        className="h-8"
                        onClick={this.toggleDiscardWarningPopup}>
                        No
                      </Button>
                      <Button
                        type="red-transparent"
                        className="h-8"
                        onClick={this.discardChanges}>
                        Yes, discard them
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </PopupWrapper>
            <Button
              type="blue"
              className="h-8 h-8 ml-8"
              // disabled={!nonNullSelectedItems}
              onClick={this.approveChanges}>
              Approve Changes
            </Button>
            <Button
              type="green"
              className="h-8 h-8 ml-8"
              onClick={this.publishChanges}
              disabled={!canPublishSelectedChanges}>
              Publish Changes
            </Button>
          </span>
        </Topbar>
        <Main className="banners_changeset__main relative">
          <Loader loading={this.state.loading}>
            <div className="inline-flex items-center bg-white p-2 rounded mb-4">
              <div className="w-4 h-4 bg-lightGreen rounded-full mr-2" />
              <div className="mr-6 text-sm">Additions</div>
              <div className="w-4 h-4 bg-lightYellow rounded-full mr-2" />
              <div className="mr-6 text-sm">Modifications</div>
              <div className="w-4 h-4 bg-lightRed rounded-full mr-2" />
              <div className="text-sm">Deletions</div>
            </div>
            <table className="text-left w-full border-collapse bg-white table-fixed">
              <thead>
                <tr>
                  <th className="p-2 w-16">
                    <Input
                      id="select_all"
                      name="select_all"
                      type={INPUT_TYPES.CHECKBOX}
                      onChange={() => this.toggleSelectAllRows(allAreSelected)}
                      checked={allAreSelected}
                      inputClassName="box_checkbox"
                    />
                  </th>
                  <th className="p-2 font-normal w-32">Updated By</th>
                  <th className="p-2 font-normal w-32">Updated On</th>
                  <th className="p-2 font-normal w-24">Type</th>
                  <th className="p-2 font-normal w-24">Variant</th>
                  <th className="p-2 font-normal">Config</th>
                  <th className="p-2 font-normal w-32 text-right">Approvals</th>
                </tr>
              </thead>
              <tbody>
                {this.state.drafts.map(draft => {
                  let totalApprovals = 0;
                  if (draft.first_approver && draft.first_approver.trim()) {
                    totalApprovals += 1;
                  }
                  if (draft.second_approver && draft.second_approver.trim()) {
                    totalApprovals += 1;
                  }
                  const key = this.getUniqueKey(draft);
                  return (
                    <ChangesetRow
                      key={key}
                      rowKey={key}
                      draft={draft}
                      totalApprovals={totalApprovals}
                      selected={this.state.selected}
                      toggleRowSelection={this.toggleRowSelection}
                    />
                  );
                })}
              </tbody>
            </table>
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withCommonUtils(withGlobalContext(ChannelingChangeset));
